import { useMemo } from 'react'
import { useUserFilters } from '@/hooks/filters/useUserFilters'
import { useCards } from '@/hooks/products/useCards'
import type { Card, SetRaritiesResponse, UserFilters } from '@/types'
import { useCollectionQuantities } from './useCollectionCrud'

const RARITY_ORDER = ['L', 'SEC', 'SP', 'SR', 'R', 'UC', 'C', 'P']

/** Cards in the set that survive the user's hidden-set/card/rarity filters. */
function visibleCardsInSet(
  allCards: Card[],
  setCode: string,
  showHidden: boolean,
  userFilters: UserFilters | undefined,
): { cards: Card[]; hiddenRarities: Set<string> } {
  const hiddenSets = new Set(userFilters?.hiddenSets ?? [])
  const hiddenCards = new Set(userFilters?.hiddenCards ?? [])
  const hiddenRarities = new Set(
    (userFilters?.hiddenRarities ?? []).filter((hr) => hr.setCode === setCode).map((hr) => hr.rarity),
  )

  let cards = allCards.filter((c) => c.setCode === setCode)
  if (!showHidden) {
    cards = cards.filter((c) => {
      if (hiddenSets.has(setCode)) return false // every card here belongs to `setCode`
      if (hiddenCards.has(c.id)) return false
      if (c.rarity && hiddenRarities.has(c.rarity)) return false
      return true
    })
  }
  return { cards, hiddenRarities }
}

function countTotalsByRarity(cardsInSet: Card[], collapseVariants: boolean): Record<string, number> {
  const totals: Record<string, number> = {}
  if (collapseVariants) {
    // Unique by (rarity, cardImageId)
    const seen = new Set<string>()
    for (const c of cardsInSet) {
      if (!c.rarity) continue
      const key = `${c.rarity}|${c.cardImageId}`
      if (seen.has(key)) continue
      seen.add(key)
      totals[c.rarity] = (totals[c.rarity] ?? 0) + 1
    }
  } else {
    for (const c of cardsInSet) {
      if (!c.rarity) continue
      totals[c.rarity] = (totals[c.rarity] ?? 0) + 1
    }
  }
  return totals
}

/** Sums owned quantity per (rarity, cardImageId) group, remembering each group's card type. */
function groupVariantQuantities(cardsInSet: Card[], collectionQty: Record<number, number>) {
  const grouped: Record<string, number> = {}
  const groupCardType: Record<string, string | null> = {}
  for (const c of cardsInSet) {
    if (!c.rarity) continue
    const qty = collectionQty[c.id] ?? 0
    if (qty === 0) continue
    const key = `${c.rarity}|${c.cardImageId}`
    grouped[key] = (grouped[key] ?? 0) + qty
    if (!(key in groupCardType)) groupCardType[key] = c.cardType
  }
  return { grouped, groupCardType }
}

/**
 * Group quantities by (rarity, cardImageId); a group counts as collected once
 * its combined quantity reaches the playset threshold (Leaders need 1 copy,
 * everything else 4).
 */
function countCollectedVariants(cardsInSet: Card[], collectionQty: Record<number, number>): Record<string, number> {
  const collected: Record<string, number> = {}
  const { grouped, groupCardType } = groupVariantQuantities(cardsInSet, collectionQty)
  for (const [key, qty] of Object.entries(grouped)) {
    const threshold = groupCardType[key] === 'Leader' ? 1 : 4
    if (qty >= threshold) {
      const rarity = key.split('|')[0]
      collected[rarity] = (collected[rarity] ?? 0) + 1
    }
  }
  return collected
}

function countCollectedCards(cardsInSet: Card[], collectionQty: Record<number, number>): Record<string, number> {
  const collected: Record<string, number> = {}
  for (const c of cardsInSet) {
    if (!c.rarity) continue
    if ((collectionQty[c.id] ?? 0) > 0) {
      collected[c.rarity] = (collected[c.rarity] ?? 0) + 1
    }
  }
  return collected
}

function countCollectedByRarity(
  cardsInSet: Card[],
  collectionQty: Record<number, number>,
  collapseVariants: boolean,
): Record<string, number> {
  return collapseVariants
    ? countCollectedVariants(cardsInSet, collectionQty)
    : countCollectedCards(cardsInSet, collectionQty)
}

function buildRarityProgress(
  totalsByRarity: Record<string, number>,
  collectedByRarity: Record<string, number>,
  hiddenRarities: Set<string>,
  showHidden: boolean,
): SetRaritiesResponse['rarities'] {
  const rarities = Object.entries(totalsByRarity)
    .map(([rarity, total]) => {
      const isHidden = hiddenRarities.has(rarity)
      if (!showHidden && isHidden) return null
      const collected = collectedByRarity[rarity] ?? 0
      return {
        rarity,
        collected,
        total,
        percentage: total > 0 ? Math.round((collected / total) * 1000) / 10 : 0,
        isHidden,
      }
    })
    .filter(Boolean) as SetRaritiesResponse['rarities']

  rarities.sort((a, b) => {
    const ai = RARITY_ORDER.indexOf(a.rarity)
    const bi = RARITY_ORDER.indexOf(b.rarity)
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })
  return rarities
}

/** Derives per-rarity progress from allCards + collectionQuantities + userFilters (no API call). */
export function useSetRarities(setCode: string | null, showHidden = false, collapseVariants = false) {
  const { data: allCards } = useCards()
  const { data: collectionQty = {} } = useCollectionQuantities()
  const { data: userFilters } = useUserFilters()

  const data = useMemo((): SetRaritiesResponse | undefined => {
    if (!setCode || !allCards) return undefined

    const { cards: cardsInSet, hiddenRarities } = visibleCardsInSet(allCards, setCode, showHidden, userFilters)
    const totalsByRarity = countTotalsByRarity(cardsInSet, collapseVariants)
    const collectedByRarity = countCollectedByRarity(cardsInSet, collectionQty, collapseVariants)
    const rarities = buildRarityProgress(totalsByRarity, collectedByRarity, hiddenRarities, showHidden)

    return { setCode, rarities }
  }, [setCode, allCards, collectionQty, userFilters, showHidden, collapseVariants])

  return { data, isLoading: !allCards }
}
